import colorVars from "@/assets/styles/colorVars"
import { BaseImage } from "@/components/image/base-image"
import { isEmpty } from "ramda"
import React, { FC, useEffect, useState } from "react"
import slugify from "slugify"
import { up } from "styled-breakpoints"
import styled, { css } from "styled-components"
import "swiper/css"
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react"

export interface TabsProps {
  activeTab?: any
  data: Array<TabsItemProps>
  handleActiveTab: (tab: number) => void
}

export interface TabsItemProps {
  icon: Queries.WpMediaItem
  title: string
}

export interface ITabItem {
  activeItem?: boolean
}

export const TabsWrapper = styled.div`
  display: none;

  ${up("lg")} {
    display: flex;
    flex-flow: column;
    gap: 8px;
    margin-top: -8px;
  }
`
export const TabName = styled.span`
  display: inline-block;
  max-width: 80px;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  color: white;
  text-align: center;
  user-select: none;
  transition: opacity 0.25s ease;

  ${up("lg")} {
    max-width: unset;
    font-size: 20px;
    line-height: 24px;
    text-align: left;
  }
`

export const TabIcon = styled(BaseImage)`
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  user-select: none;
  transition: opacity 0.25s ease;
`

export const SelectedTabItemStyle = css`
  /* stylelint-disable-next-line */
  --border-width: 1px;

  background: #212136;

  /* &:hover {
    background: #313146;
  }

  &:active {
    background: #36364b;
  } */

  ${TabIcon} {
    path {
      fill: ${colorVars.violet.p7};
    }
  }

  ${TabName} {
    font-weight: 600;
  }

  &::after {
    position: absolute;
    top: calc(-1 * var(--border-width));
    left: calc(-1 * var(--border-width));
    z-index: -1;
    width: calc(100% + var(--border-width) * 2);
    height: calc(100% + var(--border-width) * 2);
    content: "";
    background: linear-gradient(
      120deg,
      rgba(164, 150, 232, 0.8),
      rgba(164, 150, 232, 0.2)
    );
    background-size: 300% 300%;
    border-radius: 9px;
    animation: animatedgradient 4.5s ease alternate infinite;
  }

  @keyframes animatedgradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`

export const TabItem = styled.div<ITabItem>`
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-flow: column;
  gap: 7px 0;
  align-items: center;
  padding: 12px 8px;
  cursor: pointer;
  background: transparent;
  border: 1px;
  border-radius: 8px;
  transition: background 0.2s ease, box-shadow 0.2s ease;

  ${up("lg")} {
    flex-flow: row;
    gap: 12px;
    padding: 20px 16px;
  }

  &:hover {
    background: #313146;
  }

  &:not(:focus-visible) {
    box-shadow: none;
  }

  &:focus {
    box-shadow: 0 0 0 1px ${colorVars.violet.stroke50};
  }

  ${(props) => props.activeItem && SelectedTabItemStyle}
`

const SwiperWrapperShadowOverlayStyle = css`
  position: absolute;
  top: -2px;
  z-index: 5;
  display: block;
  width: 65px;
  height: calc(100% + 4px);
  pointer-events: none;
  content: "";
  transition: opacity 150ms ease;
`

const SwiperWrapper = styled(Swiper)<any>`
  position: relative;
  width: calc(100% + 32px);
  max-width: auto;
  padding: 2px 2px 2px 18px;
  margin: -17px -16px 0;

  &::before {
    ${SwiperWrapperShadowOverlayStyle}

    left: -16px;
    background: linear-gradient(90deg, rgb(2 4 22 / 100%), rgb(2 4 22 / 0%));

    ${(props) =>
      props.activeindex === 0 &&
      css`
        opacity: 0;
      `}
  }

  &::after {
    ${SwiperWrapperShadowOverlayStyle}

    right: -16px;
    background: linear-gradient(-90deg, rgb(2 4 22 / 100%), rgb(2 4 22 / 0%));

    ${(props) =>
      props.activeindex === props.totalslides - 1 &&
      css`
        opacity: 0;
      `}
  }

  ${up("md")} {
    max-width: 520px;
    margin: 0 auto;

    &::before,
    &::after {
      display: none;
    }
  }

  ${up("lg")} {
    display: none !important;
  }
`

const StyledSwiperSlide = styled(SwiperSlide)`
  max-width: 96px;
  margin: 0 4px;

  &:first-of-type {
    margin-left: 0;
  }
`

export const Tabs: FC<TabsProps> = (props) => {
  const { activeTab, data, handleActiveTab } = props

  if (isEmpty(data)) return null

  const [swiper, setSwiper] = useState<any>(null)

  useEffect(() => {
    if (swiper) {
      swiper.slideTo(activeTab)
    }
  }, [activeTab])

  return (
    <>
      <SwiperWrapper
        activeindex={activeTab}
        onSwiper={(swiper: SwiperProps) => setSwiper(swiper)}
        slidesPerView="auto"
        speed={400}
        totalslides={data.length}>
        {data.map((item: any, index: number) => {
          return (
            <StyledSwiperSlide
              key={`tab-item-${index}`}
              role="button"
              tabIndex={index + 1}>
              <TabItem
                activeItem={activeTab === index}
                key={`tab-item-${index}`}
                onClick={() => handleActiveTab(index)}
                role="button"
                tabIndex={index + 1}>
                <TabIcon alt={item.title} image={item.icon} />
                <TabName>{item.title}</TabName>
              </TabItem>
            </StyledSwiperSlide>
          )
        })}
      </SwiperWrapper>

      <TabsWrapper>
        {data.map((item: any, index: number) => {
          return (
            <TabItem
              activeItem={activeTab === index}
              key={`tab-item-${slugify(item.title)}`}
              onClick={() => handleActiveTab(index)}
              role="button"
              tabIndex={index + 1}>
              <TabIcon alt={item.title} image={item.icon} />
              <TabName>{item.title}</TabName>
            </TabItem>
          )
        })}
      </TabsWrapper>
    </>
  )
}
